// Checklist.tsx
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Checkbox as MuiCheckbox, FormControlLabel, CircularProgress } from '@mui/material';
import Colors from '../global/Colors';
import Fonts from '../global/Fonts';

const ChecklistContainer = styled.div<{ $colorMode: 'light' | 'dark' }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    background-color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.white : Colors.grey9};
    // border: 1px solid ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey1 : Colors.grey8};
    border-radius: 8px;
    // padding: 4px 0;
    gap: 2px;
`;

const ChecklistItem = styled(FormControlLabel)<{ $colorMode: 'light' | 'dark'; $interactive?: boolean }>`
    min-height: unset;
    height: auto;
    .MuiFormControlLabel-label {
        font-family: ${Fonts.lexendLight.fontFamily};
        font-weight: ${Fonts.lexendLight.fontWeight};
        font-size: 14px;
        line-height: 1.2em;
        color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey8 : Colors.grey1};
        opacity: 1;
    }
    .MuiCheckbox-root {
        color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey4 : Colors.grey6};
        &.Mui-checked {
            color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey8 : Colors.grey1};
        }
        padding: 4px 4px 4px 0;
        cursor: ${({ $interactive }) => $interactive ? 'pointer' : 'inherit'};
        pointer-events: ${({ $interactive }) => $interactive ? 'auto' : 'none'};
        &.Mui-disabled {
            // color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey4 : Colors.grey6};
            opacity: 0.8;
        }
    }
    margin: 0;
    padding: 0;
    gap: 4px;
    cursor: ${({ $interactive }) => $interactive ? 'pointer' : 'inherit'};
    /* Ensure the label doesn't get affected by the disabled state */
    &.Mui-disabled .MuiFormControlLabel-label {
        color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey8 : Colors.grey1};
        opacity: 0.8;
    }
`;

const LoadingContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 28px;
    padding-right: 3.5px;
`;

export interface ChecklistItemType {
    id: string;
    text: string;
    checked: boolean;
    interactive?: boolean;
}

export interface ChecklistProps {
    items: ChecklistItemType[];
    onItemChange: (id: string, checked: boolean) => void;
    colorMode?: 'light' | 'dark';
    showLoading?: boolean;
}

const Checklist: FunctionComponent<ChecklistProps> = ({
    items,
    onItemChange,
    colorMode = 'light',
    showLoading = false,
}) => {
    return (
        <ChecklistContainer $colorMode={colorMode}>
            {items.map((item) => (
                <ChecklistItem
                    key={item.id}
                    $colorMode={colorMode}
                    $interactive={item.interactive || false}
                    control={
                            showLoading && !item.checked ? (
                                <LoadingContainer>
                                    <CircularProgress 
                                        size={20} 
                                        thickness={6}
                                        sx={{ 
                                            color: colorMode === 'light' ? Colors.grey4 : Colors.grey6,
                                            padding: '4px'
                                        }}
                                    />
                                </LoadingContainer>
                            ) : (
                                <MuiCheckbox
                                    checked={!!item.checked}
                                    onChange={(e) => onItemChange(item.id, e.target.checked)}
                                    size="small"
                                    disabled={!(item.interactive || false)}
                                />
                            )
                    }
                    label={item.text}
                />
            ))}
        </ChecklistContainer>
    );
};

export default Checklist;